import { findTopicSuggestionByKey } from './topic-suggestions';

export function normalizeTopicKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Map a label to the canonical suggestion name when slug/name keys match. */
export function canonicalizeTopicLabel(value: string): string {
  const trimmed = value.trim().replace(/,+$/g, '');
  if (!trimmed) return '';
  const match = findTopicSuggestionByKey(trimmed);
  return match?.name ?? trimmed;
}

export function canonicalizeTopics(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const topics: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const canonical = canonicalizeTopicLabel(item);
    if (!canonical) continue;
    const key = canonical.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    topics.push(canonical);
  }
  return topics.slice(0, 12);
}

export function parseTopics(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const topics: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    topics.push(trimmed);
  }
  return topics.slice(0, 12);
}

export function topicKeys(topics: string[] | null | undefined): Set<string> {
  return new Set(parseTopics(topics).map(normalizeTopicKey));
}

export function topicsOverlap(
  left: Set<string> | string[] | null | undefined,
  right: Set<string> | string[] | null | undefined,
): boolean {
  const leftKeys = left instanceof Set ? left : topicKeys(left ?? []);
  const rightKeys = right instanceof Set ? right : topicKeys(right ?? []);
  for (const key of leftKeys) {
    if (rightKeys.has(key)) return true;
  }
  return false;
}

export function countTopicOverlap(
  viewerTopics: Set<string> | string[] | null | undefined,
  authorTopics: string[] | null | undefined,
): number {
  const viewerKeys = viewerTopics instanceof Set ? viewerTopics : topicKeys(viewerTopics ?? []);
  const authorKeys = topicKeys(authorTopics);
  let count = 0;
  for (const key of authorKeys) {
    if (viewerKeys.has(key)) count += 1;
  }
  return count;
}

/** Prefer canonical display name when aggregating topic search matches by key. */
export function preferCanonicalTopicLabel(labels: string[]): string {
  const parsed = parseTopics(labels);
  if (!parsed.length) return '';
  for (const label of parsed) {
    const match = findTopicSuggestionByKey(label);
    if (match) return match.name;
  }
  return parsed[0]!;
}
